import { useEffect, useState } from "react";
import type { ViewTitle } from "../context";

const HEADER_TITLE_SELECTOR = "#scroll-container-header h1";
const FOOTER_SELECTOR = "#scroll-container-footer";

/**
 * Whether focus was lost by the step change (left on `body` or a removed
 * node) or sits on the shell footer that triggered it. Focus inside the step
 * itself (an input the step focused on mount) is left alone.
 */
function shouldMoveFocusToTitle(): boolean {
  const active = document.activeElement;
  if (active === null || active === document.body || !active.isConnected) {
    return true;
  }
  return document.querySelector(FOOTER_SELECTOR)?.contains(active) === true;
}

/**
 * Shell step-change accessibility (FC-24): when the published step title
 * changes, a polite live region announces it and focus moves to the header
 * `h1` (made programmatically focusable) unless the new step already placed
 * focus inside its content. Renders a visually hidden status region.
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * const { viewTitle } = useSelfService();
 * <StepAnnouncer viewTitle={viewTitle} />
 * ```
 */
export function StepAnnouncer({ viewTitle }: { readonly viewTitle: ViewTitle | null }) {
  const [announcement, setAnnouncement] = useState("");

  useEffect(
    function announceAndFocusStepTitle() {
      if (viewTitle === null) return;
      // Wait one frame so the header has committed the new heading.
      const frame = window.requestAnimationFrame(function focusStepTitle() {
        const heading = document.querySelector<HTMLElement>(HEADER_TITLE_SELECTOR);
        if (heading === null) return;
        setAnnouncement(heading.textContent?.trim() ?? "");
        if (!shouldMoveFocusToTitle()) return;
        if (!heading.hasAttribute("tabindex")) heading.setAttribute("tabindex", "-1");
        heading.focus({ preventScroll: true });
      });
      return function cancelStepTitleFocus() {
        window.cancelAnimationFrame(frame);
      };
    },
    [viewTitle],
  );

  return (
    <div
      data-testid="frontdesk-self-service-step-announcer"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="sr-only"
    >
      {announcement}
    </div>
  );
}
